import React, { useMemo, useCallback } from "react";
import { StyleSheet, View } from "react-native";

import { useTheme } from "../../utils/theme";
import { Text } from "../Text";

import type { TestProps } from "../../types";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";
import type { ReactText } from "react";

type LegacySize = "title" | "title2" | "title3" | "title4" | "title5";
type Size =
  | "semibold.title.one"
  | "semibold.title.two"
  | "semibold.title.three"
  | "semibold.title.four"
  | "semibold.title.five";

const sizeTransform: Record<LegacySize, Size> = {
  title: "semibold.title.one",
  title2: "semibold.title.two",
  title3: "semibold.title.three",
  title4: "semibold.title.four",
  title5: "semibold.title.five",
};

export interface StatusBadgeProps extends TestProps {
  /**
   * text to show for the "label"
   */
  children: ReactText;
  /**
   * sets the color based on status
   */
  status?: "default" | "success" | "knowledge" | "warning" | "error";
  /**
   * whether or not to have a solid or outlined badge
   */
  variation?: "default" | "outlined";
  /**
   * size of the badge based off of the text near the element
   * @deprecated use `size` prop instead
   */
  legacySize?: LegacySize;
  /**
   * size of the badge based off of the text near the element
   */
  size?: Size;
  /**
   * override styles (not advised)
   */
  style?: StyleProp<ViewStyle>;
}

/**
 * A status badge component with default styles.
 *
 * example: https://smartrent-ui.com/components/general/status-badge
 */
export function StatusBadge(
  props: Omit<StatusBadgeProps, "legacySize">
): JSX.Element;
export function StatusBadge(props: Omit<StatusBadgeProps, "size">): JSX.Element;
export function StatusBadge({
  children,
  style,
  status = "default",
  variation = "default",
  legacySize,
  size,
  ...rest
}: StatusBadgeProps) {
  const { colors, isLightMode, fonts, rounding, spacing } = useTheme();

  const getStatusColor = useCallback(
    (status: "success" | "knowledge" | "warning" | "error" | "default") => {
      if (status === "default") {
        return isLightMode ? colors.gray600 : colors.gray400;
      }

      return colors[`${status === "knowledge" ? "info" : status}Primary`];
    },
    [colors, isLightMode]
  );

  const { container, text } = useMemo(() => {
    const container: ViewStyle = {};
    const text: TextStyle = {
      letterSpacing: 0.25,
      color: !isLightMode
        ? colors.gray800
        : status === "warning"
          ? colors.warning900
          : colors.white,
    };

    // added the assignment to make typescript happy since the function overloading does this for us
    const normalizedSize = legacySize
      ? sizeTransform[legacySize]
      : (size as Size);

    if (normalizedSize === "semibold.title.one") {
      text.fontSize = 14;
      text.lineHeight = 20;
    } else {
      text.fontSize = 12;
      text.lineHeight = 16;
    }

    switch (normalizedSize) {
      case "semibold.title.one":
        container.height = 36;
        break;
      case "semibold.title.two":
        container.height = 32;
        break;
      case "semibold.title.three":
        container.height = 28;
        break;
      case "semibold.title.four":
        container.height = 20;
        break;
      case "semibold.title.five":
        container.height = 16;
        break;
    }

    switch (variation) {
      case "outlined":
        container.borderColor = getStatusColor(status);
        text.color =
          isLightMode && status === "warning"
            ? colors.warning900
            : getStatusColor(status);
        break;

      default:
        container.backgroundColor = getStatusColor(status);
        container.borderColor = getStatusColor(status);
        break;
    }

    return {
      container,
      text,
    };
  }, [
    isLightMode,
    colors,
    legacySize,
    size,
    variation,
    getStatusColor,
    status,
  ]);

  return (
    <View
      style={[
        styles.root,
        { borderRadius: rounding.md, paddingHorizontal: spacing.sm },
        container,
        style,
      ]}
      {...rest}
    >
      <Text style={[styles.text, fonts.semibold, text]}>
        {children.toString().toUpperCase()}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 3, // This should be 4, but we'll use 1px for the border of the pill
    borderWidth: 1,
  },
  text: {
    fontSize: 12,
    lineHeight: 16,
  },
});
